import React, { useState } from 'react';
import { Button, Empty, Select, Switch, Tag, Tooltip, Typography } from 'antd';
import {
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
} from '@ant-design/icons';
import RichTextEditor, {
	RichTextViewer,
} from '../../components/RichTextEditor';
import {
	CAUGHT_PHASE_OPTIONS,
	DEFAULT_CAUGHT_PHASE,
	DEFAULT_FAULT,
	DEFAULT_SEVERITY,
	SEVERITY_COLORS,
	SEVERITY_OPTIONS,
	faultOptions,
	isIssueWorthSaving,
} from './issueFields';
import type { ReviewIssueDraft } from './issueFields';
import {
	PROGRAM_ISSUE_CAUGHT_PHASE_LABELS,
	PROGRAM_ISSUE_FAULT_LABELS,
	PROGRAM_ISSUE_SEVERITY_LABELS,
} from '../../interfaces/CamProgramming.interface';
import type {
	Program,
	ProgramIssueCaughtPhase,
} from '../../interfaces/CamProgramming.interface';
import type { ProgramImageHandlers } from './useProgramImages';

const { Text } = Typography;

let draftCount = 0;
const nextDraftKey = () => `new-${(draftCount += 1)}`;

interface ReviewIssuesFieldProps {
	/** Supplied by the enclosing `Form.Item`. */
	value?: ReviewIssueDraft[];
	onChange?: (value: ReviewIssueDraft[]) => void;
	/** Image handlers for the description editor, from `useProgramImages`. */
	images: ProgramImageHandlers;
	/** When set, new issues use this phase and the field cannot be changed. */
	lockedCaughtPhase?: ProgramIssueCaughtPhase;
	/** The program under review, so fault options can name the people on it. */
	program?: Program;
}

/**
 * The issues raised by a review, edited alongside it. A review is often
 * nothing but its findings, so this has to be reachable while logging one
 * rather than only afterwards, from the saved review's own issues list.
 *
 * Every keystroke lands straight in the field's value, so whatever is on
 * screen is what gets written when the review is saved. There is no separate
 * step to confirm an issue — closing its editor only collapses it.
 */
const ReviewIssuesField: React.FC<ReviewIssuesFieldProps> = ({
	value = [],
	onChange,
	images,
	lockedCaughtPhase,
	program,
}) => {
	const [editingKey, setEditingKey] = useState<string | null>(null);
	const defaultCaughtPhase = lockedCaughtPhase ?? DEFAULT_CAUGHT_PHASE;
	const faults = faultOptions(program);

	/**
	 * Sheds rows that were opened and left empty, keeping whichever is still
	 * being written. Without this an accidental "Log Issue" would leave an empty
	 * card sitting in the list until save time quietly dropped it.
	 */
	const settled = (keepKey: string | null = null) =>
		value.filter((issue) => issue.key === keepKey || isIssueWorthSaving(issue));

	const patch = (key: string, changes: Partial<ReviewIssueDraft>) =>
		onChange?.(
			value.map((issue) =>
				issue.key === key ? { ...issue, ...changes } : issue
			)
		);

	const add = () => {
		const issue: ReviewIssueDraft = {
			key: nextDraftKey(),
			severity: DEFAULT_SEVERITY,
			caught_phase: defaultCaughtPhase,
			fault: DEFAULT_FAULT,
			insufficient_training: false,
			description: '',
		};
		onChange?.([...settled(), issue]);
		setEditingKey(issue.key);
	};

	const edit = (key: string) => {
		onChange?.(
			settled().map((issue) =>
				issue.key === key && lockedCaughtPhase
					? { ...issue, caught_phase: lockedCaughtPhase }
					: issue
			)
		);
		setEditingKey(key);
	};

	const collapse = () => {
		onChange?.(settled());
		setEditingKey(null);
	};

	const remove = (key: string) => {
		onChange?.(value.filter((issue) => issue.key !== key));
		if (editingKey === key) setEditingKey(null);
	};

	const removeButton = (key: string) => (
		<Tooltip title="Remove">
			<Button
				type="text"
				size="small"
				danger
				icon={<DeleteOutlined />}
				onClick={() => remove(key)}
			/>
		</Tooltip>
	);

	return (
		<div>
			<div className="cam-section__header cam-review-issues__header">
				<span className="cam-section__title cam-review-issues__title">
					Issues
					{value.length > 0 && (
						<span className="cam-section__count">{value.length}</span>
					)}
				</span>
				<Button
					size="small"
					icon={<PlusOutlined />}
					disabled={editingKey !== null}
					onClick={add}
				>
					Log Issue
				</Button>
			</div>

			<Text type="secondary" className="cam-review-issues__hint">
				Saved together with the review.
			</Text>

			{value.length === 0 ? (
				<div className="cam-section__placeholder">
					<Empty
						image={Empty.PRESENTED_IMAGE_SIMPLE}
						description="No issues on this review"
					/>
				</div>
			) : (
				<div className="cam-entry-list">
					{value.map((issue) =>
						issue.key === editingKey ? (
							<div className="cam-issue-draft" key={issue.key}>
								<div className="cam-issue-draft__row">
									<div className="cam-issue-draft__severity">
										<label className="detail-label">Severity</label>
										<Select
											value={issue.severity}
											onChange={(severity) => patch(issue.key, { severity })}
											options={SEVERITY_OPTIONS}
											style={{ width: '100%' }}
										/>
									</div>
									<div className="cam-issue-draft__severity">
										<label className="detail-label">Caught In</label>
										<Select
											value={lockedCaughtPhase ?? issue.caught_phase}
											onChange={(caught_phase) =>
												patch(issue.key, { caught_phase })
											}
											options={CAUGHT_PHASE_OPTIONS}
											style={{ width: '100%' }}
											disabled={!!lockedCaughtPhase}
										/>
									</div>
								</div>

								<div className="cam-issue-draft__row">
									<div className="cam-issue-draft__fault">
										<label className="detail-label">At Fault</label>
										<Select
											value={issue.fault}
											onChange={(fault) => patch(issue.key, { fault })}
											options={faults}
											style={{ width: '100%' }}
										/>
									</div>
									<div className="cam-issue-draft__training">
										<label className="detail-label">
											Insufficient Training
										</label>
										<Tooltip title="Marks the issue as a training gap, which takes the charge off whoever is at fault.">
											<Switch
												checked={issue.insufficient_training}
												onChange={(insufficient_training) =>
													patch(issue.key, { insufficient_training })
												}
												checkedChildren="Yes"
												unCheckedChildren="No"
											/>
										</Tooltip>
									</div>
								</div>

								<label className="detail-label">Description</label>
								{/* Keyed so opening a different issue reloads the editor
								    rather than carrying the previous one's text over. */}
								<RichTextEditor
									key={issue.key}
									content={issue.description}
									onChange={(description) => patch(issue.key, { description })}
									{...images}
									placeholder="Describe the issue... Paste or drop images directly."
									minHeight={140}
								/>

								<div className="cam-issue-draft__actions">
									<Button
										danger
										icon={<DeleteOutlined />}
										onClick={() => remove(issue.key)}
									>
										Delete
									</Button>
									<Button type="primary" onClick={collapse}>
										Add
									</Button>
								</div>
							</div>
						) : (
							<div className="cam-entry" key={issue.key}>
								<div className="cam-entry__header">
									<div className="cam-entry__tags">
										<Tag
											color={SEVERITY_COLORS[issue.severity]}
											bordered={false}
										>
											{PROGRAM_ISSUE_SEVERITY_LABELS[issue.severity]}
										</Tag>
										<Tag bordered={false}>
											{PROGRAM_ISSUE_CAUGHT_PHASE_LABELS[
												lockedCaughtPhase ?? issue.caught_phase
											]}
										</Tag>
										{issue.fault !== DEFAULT_FAULT && (
											<Tag color="purple" bordered={false}>
												{PROGRAM_ISSUE_FAULT_LABELS[issue.fault]} at fault
											</Tag>
										)}
										{issue.insufficient_training && (
											<Tag color="blue" bordered={false}>
												Insufficient training
											</Tag>
										)}
									</div>
									<div className="cam-entry__actions">
										<Tooltip title="Edit">
											<Button
												type="text"
												size="small"
												icon={<EditOutlined />}
												onClick={() => edit(issue.key)}
											/>
										</Tooltip>
										{removeButton(issue.key)}
									</div>
								</div>
								<RichTextViewer
									html={issue.description}
									emptyText="No description"
									className="cam-entry__body"
								/>
							</div>
						)
					)}
				</div>
			)}
		</div>
	);
};

export default ReviewIssuesField;
